import { useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from "@fluentui/react-components";
import { devices } from "library/constants";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { UploadIcon } from "components/UploadIcon/UploadIcon";
import { useAppTheme } from "hooks/useAppTheme";

import avatar01 from "assets/avatars/Avatar_seperated_benefitHUB-01.png";
import avatar02 from "assets/avatars/Avatar_seperated_benefitHUB-02.png";
import avatar03 from "assets/avatars/Avatar_seperated_benefitHUB-03.png";
import avatar04 from "assets/avatars/Avatar_seperated_benefitHUB-04.png";
import avatar05 from "assets/avatars/Avatar_seperated_benefitHUB-05.png";
import avatar06 from "assets/avatars/Avatar_seperated_benefitHUB-06.png";
import avatar07 from "assets/avatars/Avatar_seperated_benefitHUB-07.png";
import avatar08 from "assets/avatars/Avatar_seperated_benefitHUB-08.png";
import avatar09 from "assets/avatars/Avatar_seperated_benefitHUB-09.png";
import avatar10 from "assets/avatars/Avatar_seperated_benefitHUB-10.png";
import avatar11 from "assets/avatars/Avatar_seperated_benefitHUB-11.png";
import avatar12 from "assets/avatars/Avatar_seperated_benefitHUB-12.png";
import avatar13 from "assets/avatars/Avatar_seperated_benefitHUB-13.png";
import avatar14 from "assets/avatars/Avatar_seperated_benefitHUB-14.png";
import avatar15 from "assets/avatars/Avatar_seperated_benefitHUB-15.png";
import avatar16 from "assets/avatars/Avatar_seperated_benefitHUB-16.png";
import avatar17 from "assets/avatars/Avatar_seperated_benefitHUB-17.png";
import avatar18 from "assets/avatars/Avatar_seperated_benefitHUB-18.png";
import avatar19 from "assets/avatars/Avatar_seperated_benefitHUB-19.png";
import avatar20 from "assets/avatars/Avatar_seperated_benefitHUB-20.png";
import avatar21 from "assets/avatars/Avatar_seperated_benefitHUB-21.png";
import avatar22 from "assets/avatars/Avatar_seperated_benefitHUB-22.png";
import avatar23 from "assets/avatars/Avatar_seperated_benefitHUB-23.png";
import avatar24 from "assets/avatars/Avatar_seperated_benefitHUB-24.png";
import avatar25 from "assets/avatars/Avatar_seperated_benefitHUB-25.png";
import avatar26 from "assets/avatars/Avatar_seperated_benefitHUB-26.png";
import avatar27 from "assets/avatars/Avatar_seperated_benefitHUB-27.png";
import avatar28 from "assets/avatars/Avatar_seperated_benefitHUB-28.png";
import avatar29 from "assets/avatars/Avatar_seperated_benefitHUB-29.png";
import avatar30 from "assets/avatars/Avatar_seperated_benefitHUB-30.png";
import avatar31 from "assets/avatars/Avatar_seperated_benefitHUB-31.png";
import avatar32 from "assets/avatars/Avatar_seperated_benefitHUB-32.png";
import avatar33 from "assets/avatars/Avatar_seperated_benefitHUB-33.png";
import avatar34 from "assets/avatars/Avatar_seperated_benefitHUB-34.png";
import avatar35 from "assets/avatars/Avatar_seperated_benefitHUB-35.png";
import avatar36 from "assets/avatars/Avatar_seperated_benefitHUB-36.png";
import avatar37 from "assets/avatars/Avatar_seperated_benefitHUB-37.png";
import avatar38 from "assets/avatars/Avatar_seperated_benefitHUB-38.png";
import avatar39 from "assets/avatars/Avatar_seperated_benefitHUB-39.png";
import avatar40 from "assets/avatars/Avatar_seperated_benefitHUB-40.png";
import avatar41 from "assets/avatars/Avatar_seperated_benefitHUB-41.png";
import avatar42 from "assets/avatars/Avatar_seperated_benefitHUB-42.png";
import avatar43 from "assets/avatars/Avatar_seperated_benefitHUB-43.png";

const PRESET_AVATARS = [
  avatar01,
  avatar02,
  avatar03,
  avatar04,
  avatar05,
  avatar06,
  avatar07,
  avatar08,
  avatar09,
  avatar10,
  avatar11,
  avatar12,
  avatar13,
  avatar14,
  avatar15,
  avatar16,
  avatar17,
  avatar18,
  avatar19,
  avatar20,
  avatar21,
  avatar22,
  avatar23,
  avatar24,
  avatar25,
  avatar26,
  avatar27,
  avatar28,
  avatar29,
  avatar30,
  avatar31,
  avatar32,
  avatar33,
  avatar34,
  avatar35,
  avatar36,
  avatar37,
  avatar38,
  avatar39,
  avatar40,
  avatar41,
  avatar42,
  avatar43,
];

type FormattedFile = {
  base64String: string;
  name: string;
  type: string;
  path: string;
};

type Props = {
  isOpen: boolean;
  savedAvatarName?: string;
  onClose: () => void;
  onSelect: (file: FormattedFile) => void;
};

async function urlToFormattedFile(url: string): Promise<FormattedFile> {
  const response = await fetch(url);
  const blob = await response.blob();
  const name = url.split("/").pop() || "avatar.png";
  const base64String = await new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]);
    };
    reader.readAsDataURL(blob);
  });
  return { base64String, name, type: blob.type, path: "" };
}

async function fileToFormattedFile(file: File): Promise<FormattedFile> {
  const base64String = await new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]);
    };
    reader.readAsDataURL(file);
  });
  return { base64String, name: file.name, type: file.type, path: "" };
}

export function AvatarPickerDialog({ isOpen, savedAvatarName, onClose, onSelect }: Props) {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const savedIndex = useMemo(
    () =>
      savedAvatarName
        ? PRESET_AVATARS.findIndex((url) => url.split("/").pop() === savedAvatarName)
        : -1,
    [savedAvatarName]
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(savedIndex >= 0 ? savedIndex : null);
    }
  }, [isOpen, savedIndex]);

  const handleAvatarClick = (index: number) => {
    setSelectedIndex(index);
  };

  const handleConfirm = async () => {
    if (selectedIndex === null) return;
    setLoading(true);
    try {
      const formatted = await urlToFormattedFile(PRESET_AVATARS[selectedIndex]);
      onSelect(formatted);
      onClose();
      setSelectedIndex(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const formatted = await fileToFormattedFile(file);
      onSelect(formatted);
      onClose();
      setSelectedIndex(null);
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <StyledDialogSurface>
        <DialogBody>
          <DialogTitle>{t("hbh.chooseAvatar.label")}</DialogTitle>
          <DialogContent>
            <AvatarGrid data-help="profile-avatar-presets">
              {PRESET_AVATARS.map((src, i) => (
                <AvatarOptionWrap key={i}>
                  <AvatarOption
                    selected={selectedIndex === i}
                    selectedColor={theme.palette.themePrimary}
                    onClick={() => handleAvatarClick(i)}
                  >
                    <img src={src} alt={`Avatar ${i + 1}`} />
                  </AvatarOption>
                  {savedIndex === i && (
                    <SavedBadge color={theme.palette.themePrimary}>✓</SavedBadge>
                  )}
                </AvatarOptionWrap>
              ))}
            </AvatarGrid>

            <UploadRow>
              <Button
                data-help="profile-avatar-upload"
                appearance="outline"
                icon={<UploadIcon fill={theme.palette.themePrimary} />}
                onClick={() => fileInputRef.current?.click()}
              >
                {t("hbh.uploadFromDevice.label")}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
            </UploadRow>
          </DialogContent>

          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={onClose}>
                {t("greco.cancel")}
              </Button>
            </DialogTrigger>
            <Button
              data-help="profile-avatar-confirm"
              appearance="primary"
              disabled={selectedIndex === null || loading}
              onClick={handleConfirm}
            >
              {t("greco.confirm")}
            </Button>
          </DialogActions>
        </DialogBody>
      </StyledDialogSurface>
    </Dialog>
  );
}

const AvatarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin-top: 16px;
  box-sizing: border-box;
  padding: 5px;
  gap: 12px;
  margin-bottom: 20px;
  overflow-y: auto;
  max-height: 70vh;
  @media only screen and ${devices.md} {
    grid-template-columns: repeat(8, 1fr);
  }
`;

const AvatarOptionWrap = styled.div`
  position: relative;
`;

const SavedBadge = styled.span<{ color: string }>`
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ color }) => color};
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
`;

const AvatarOption = styled.div<{ selected: boolean; selectedColor: string }>`
  cursor: pointer;
  border-radius: 50%;
  border: 3px solid ${({ selected, selectedColor }) => (selected ? selectedColor : "transparent")};
  box-shadow: ${({ selected }) =>
    selected ? "0 0 0 2px rgba(0,0,0,0.15)" : "0 2px 6px rgba(0,0,0,0.1)"};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  overflow: hidden;
  aspect-ratio: 1;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.22);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const StyledDialogSurface = styled(DialogSurface)`
  max-width: 800px;
  min-width: 800px;
  box-sizing: border-box;
  align-self: center;

  @media not all and ${devices.sm} {
    margin: auto 16px;
    min-width: calc(100% - 32px);
    width: calc(100% - 32px);
  }
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
`;
